import { ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { asyncScheduler, Observable, scheduled } from 'rxjs';

import { PageableTableComponent } from '../../../../../shared/controls/pageable-table/pageable-table.component';
import { ModalComponent } from '../../../../../shared/controls/modal/modal.component';
import { AutoCompleteDropdownComponent } from '../../../../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';
import { WizardStep } from '../../../../../shared/controls/wizard-control/wizard-step.interface';

import { ToastService } from '../../../../../shared/toast/toast.service';
import { ErrorService } from '../../../../../shared/services/error-service';
import { InitializerService } from '../../../../../shared/services/initializer-service';
import { LoadingService } from '../../../../../shared/services/loading.service';

import { MatchingService, PagingData, AnnexureMatchingItem, AgeingMatchingItem, CreateMatchingDataInput, AnnexureService, AgeingService, AnnexureDataResult, AgeingDataResult, UpdateAnnexureDataInput, UpdateAgeingDataInput, NoteService, CreateAgeingNoteInput, API_BASE_URL } from '../../../../../../service/proxy';

@Component({
    selector: 'matching-records',
    templateUrl: './matching-records.component.html',
    styleUrls: ['./matching-records.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        PageableTableComponent,
        ModalComponent,
        AutoCompleteDropdownComponent
    ],
    providers: [
        MatchingService,
        AnnexureService,
        AgeingService,
        NoteService
    ]
})
export class MatchingRecordsComponent implements OnInit, WizardStep {
    @ViewChild('form')
    form!: NgForm;

    public matchingBatchId: number = 0;
    public matchingBatchDetails: any = {};
    public initialized = false;
    public loading: boolean = false;
    public loadingAnnexure: boolean = false;
    public loadingAgeing: boolean = false;

    public annexurePaging: PagingData = new PagingData();
    public ageingPaging: PagingData = new PagingData();

    public search: string = '';
    public selectedFilter: string = '';

    public annexureSearch: string = '';
    public ageingSearch: string = '';

    public annexureRows: AnnexureMatchingItem[] = [];
    public ageingRows: AgeingMatchingItem[] = [];

    public selectedAnnexureId: number = 0;
    public selectedAgeingIds: number[] = [];

    public createMatchingDataInput: CreateMatchingDataInput = new CreateMatchingDataInput({
        annexureDataId: 0,
        matchingBatchId: 0,
        ageingDataIds: [],
    });

    public matchedAgeingIdsForAnnexure: number[] = [];

    public highlightedAnnexureIds: number[] = [];
    public highlightedAgeingIds: number[] = [];

    public navigatingToMatch: boolean = false;

    public matched: boolean = false;
    public autoMatched: boolean = false;
    public uncategorized: boolean = false;
    public franchiseLoss: boolean = false;

    public period: string = '';
    public clientName: string = '';
    public clientAreaName: string = '';

    public periodId: number = 0;
    public clientId: number = 0;
    public clientAreaId: number = 0;

    public isUpdateAnnexure: boolean = false;
    public isUpdateAgeing: boolean = false;

    public annexureRecord: AnnexureDataResult = new AnnexureDataResult();
    public ageingRecord: AgeingDataResult = new AgeingDataResult();

    public ageingModalHeader: string = 'Amend Ageing Listing';
    public annexureModalHeader: string = 'Amend Annexure Listing';

    public noteOptions: { id: number; description: string; showOther: boolean }[] = [];
    public selectedNoteId: number | undefined = undefined;
    public showOtherInput: boolean = false;

    private baseUrl: string;

    constructor(
        private route: ActivatedRoute,
        private cdr: ChangeDetectorRef,
        private http: HttpClient,
        private errorService: ErrorService,
        private initializerService: InitializerService,
        private matchingService: MatchingService,
        private annexureService: AnnexureService,
        private ageingService: AgeingService,
        private noteService: NoteService,
        private toastService: ToastService,
        private loadingService: LoadingService,
        @Inject(API_BASE_URL) baseUrl: string)
    {
        this.baseUrl = baseUrl;
        this.annexurePaging = this.initializerService.createPagingData();
        this.ageingPaging = this.initializerService.createPagingData();
    }

    ngOnInit(): void {
        // Data loaded in onActivate method
    }

    onActivate(): void {
        this.matchingBatchId = Number(this.route.snapshot.paramMap.get('id'));

        this.initialized = true;
        this.selectedAgeingIds = [];
        this.selectedAnnexureId = 0;

        if (this.matchingBatchId) {
            this.getMatchingBatchDetails(this.matchingBatchId);
            this.getNoteOptions();
            this.loadFilter(); // refreshGrids is called inside loadFilter once filter state is known
        } else {
            this.refreshGrids();
        }
    }

    getNoteOptions(): void {
        this.noteService.getAllNotes()
            .subscribe({
                next: (result) => {
                    this.noteOptions = result?.results || [];
                },
                error: (error) => {
                    this.errorService.handleError(error);
                }
            });
    }

    onNoteSelectionChange(selectedNoteId: number | null) {
        this.selectedNoteId = selectedNoteId || 0;
        const selectedNote = this.noteOptions.find(note => note.id === this.selectedNoteId);

        this.showOtherInput = selectedNote?.showOther || false;

        if (selectedNote && !selectedNote.showOther) {
            this.ageingRecord['comment'] = selectedNote.description;
        }
        else if (selectedNote && selectedNote.showOther) {
            this.ageingRecord['comment'] = '';
        }
    }

    getNoteDisplayText = (note: { id: number; description: string; showOther: boolean; }): string => {
        return note.description;
    };

    loadFilter(): void {
        // Load persisted filter state then refresh grids so they always
        // load AFTER filter state is known (avoids race condition with refreshGrids).
        this.http.get<any>(`${this.baseUrl}/matching/${this.matchingBatchId}/filter`)
            .subscribe({
                next: (filter: any) => {
                    if (filter) {
                        this.annexureSearch = filter.searchAnnexuresValue || '';
                        this.ageingSearch = filter.searchAgeingsValue || '';
                        this.franchiseLoss = filter.filterByBelowFranchiseLoss ?? false;
                        this.matched = filter.filterByMatchedRecords ?? false;
                        this.autoMatched = filter.filterByAutoMatched ?? false;
                        this.uncategorized = filter.filterByNonCategorized ?? false;
                    }
                    this.refreshGrids();
                },
                error: (error) => {
                    console.error('Error loading filter:', error);
                    this.refreshGrids(); // load with defaults on error
                }
            });
    }

    saveFilter(): void {
        // Persist filter state for this matching batch
        const filterData = {
            matchingBatchId: this.matchingBatchId,
            searchValue: '', // Legacy field - not used
            searchAnnexuresValue: this.annexureSearch,
            searchAgeingsValue: this.ageingSearch,
            filterByBelowFranchiseLoss: this.franchiseLoss,
            filterByMatchedRecords: this.matched,
            filterByAutoMatched: this.autoMatched,
            filterByNonCategorized: this.uncategorized,
            annexureSortBy: this.annexurePaging.sortColumn,
            annexureSortAsc: this.annexurePaging.sortDirection === 'asc',
            ageingSortBy: this.ageingPaging.sortColumn,
            ageingSortAsc: this.ageingPaging.sortDirection === 'asc'
        };

        this.http.post<any>(`${this.baseUrl}/matching/${this.matchingBatchId}/filter`, filterData)
            .subscribe({
                next: () => {
                    // Filter saved successfully - no need to notify user
                },
                error: (error) => {
                    console.error('Error saving filter:', error);
                    // Don't show error toast - not critical
                }
            });
    }

    refreshGrids(){
        // Reset page numbers to 1 when global filters change
        this.annexurePaging.pageNo = 1;
        this.ageingPaging.pageNo = 1;

        this.getAnnexureData(this.annexurePaging);
        this.getAgeingData(this.ageingPaging);

        this.selectedAgeingIds = [];
        this.selectedAnnexureId = 0;

        // Save filter state after changes
        this.saveFilter();

        this.cdr.detectChanges();
    }

    searchAnnexure(): void {
        this.annexurePaging.pageNo = 1;
        this.getAnnexureData(this.annexurePaging);

        this.selectedAgeingIds = [];
        this.selectedAnnexureId = 0;

        this.saveFilter();
        this.cdr.detectChanges();
    }

    searchAgeing(): void {
        this.ageingPaging.pageNo = 1;
        this.getAgeingData(this.ageingPaging);

        this.selectedAgeingIds = [];
        this.selectedAnnexureId = 0;

        this.saveFilter();
        this.cdr.detectChanges();
    }

    onSearch(): void {
        const term = this.search.trim();
        switch (this.selectedFilter) {
            case 'annexure':
                this.annexureSearch = term;
                this.searchAnnexure();
                this.toastService.show(term ? `Annexure filter applied: "${term}"` : 'Annexure filter cleared', 'info');
                break;
            case 'ageing':
                this.ageingSearch = term;
                this.searchAgeing();
                this.toastService.show(term ? `Ageing filter applied: "${term}"` : 'Ageing filter cleared', 'info');
                break;
            default: // '' = Both
                this.annexureSearch = term;
                this.ageingSearch = term;
                this.refreshGrids();
                this.toastService.show(term ? `Filter applied to both grids: "${term}"` : 'Filters cleared', 'info');
                break;
        }
    }

    onFilterDropdownChange(): void {
        if (this.selectedFilter === 'clear') {
            this.annexureSearch = '';
            this.ageingSearch = '';
            this.search = '';
            this.selectedFilter = '';
            this.refreshGrids();
            this.toastService.show('Filters cleared', 'info');
        }
    }

    refreshCurrentPage(): void {
        // OLD SYSTEM BEHAVIOR: Preserve page position after match/unmatch operations
        // Refresh both grids at their current page numbers
        this.getAnnexureData(this.annexurePaging);
        this.getAgeingData(this.ageingPaging);

        this.selectedAgeingIds = [];
        this.selectedAnnexureId = 0;
        this.highlightedAnnexureIds = [];
        this.highlightedAgeingIds = [];

        this.cdr.detectChanges();
    }

    getMatchingBatchDetails(batchId: number): void {
        this.matchingService.getMatchingBatchDetails(batchId)
            .subscribe((result) => {
                this.matchingBatchDetails = result;

                if (this.matchingBatchDetails) {
                    this.clientName = this.matchingBatchDetails.client;
                    this.clientAreaName = this.matchingBatchDetails.clientArea;
                    this.period = this.matchingBatchDetails.period;
                }
            }
        );
    }

    getAnnexureData(pagingData: PagingData = new PagingData): void {
        this.annexurePaging = pagingData;

        if (!this.initialized) {
            return;
        }

        this.loadingAnnexure = true;
        this.updateLoading();
        this.annexureRows = [];

        this.matchingService.getAnnexureMatchingData(this.annexureSearch, pagingData.pageNo, pagingData.pageSize, pagingData.sortDirection, pagingData.sortColumn, this.matchingBatchId)
            .subscribe({
                next: (data) => {
                    this.annexurePaging = data.pagingData;

                    let results = (data.results || []).map(row => {
                        const newRow = new AnnexureMatchingItem({ ...row });

                        if (newRow.approvedLimit !== null && newRow.approvedLimit !== undefined) {
                            newRow.approvedLimit = this.formatCurrency(newRow.approvedLimit);
                        }

                        return newRow;
                    });

                    // OLD SYSTEM BEHAVIOR: Annexure grid shows ALL records
                    // The 'matched' filter only applies to Ageing grid
                    this.annexureRows = results;

                    this.loadingAnnexure = false;
                    this.updateLoading();
                },
                error: (error) => {
                    this.loadingAnnexure = false;
                    this.updateLoading();

                    if (error.status === 204) {
                        this.toastService.show('No annexure data found', 'info');
                    }
                    else if (error.status === 404) {
                        this.toastService.show('Matching batch not found', 'error');
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                }
            }
        );
    }

    getAgeingData(pagingData: PagingData = new PagingData): void {
        this.ageingPaging = pagingData;

        if (!this.initialized) {
            return;
        }

        this.loadingAgeing = true;
        this.updateLoading();
        this.ageingRows = [];

        this.matchingService.getAgeingMatchingData(this.ageingSearch, pagingData.pageNo, pagingData.pageSize, pagingData.sortDirection, pagingData.sortColumn, this.matchingBatchId, this.matched, this.franchiseLoss, this.uncategorized)
            .subscribe({
                next: (data) => {
                    this.ageingPaging = data.pagingData;

                    this.ageingRows = (data.results || []).map(row => {
                        if (row['balanceCurrent'] !== null && row['balanceCurrent'] !== undefined) {
                            row['balanceCurrent'] = this.formatCurrency(row['balanceCurrent']);
                        }

                        if (row['total'] !== null && row['total'] !== undefined) {
                            row['total'] = this.formatCurrency(row['total']);
                        }

                        return row;
                    });

                    this.loadingAgeing = false;
                    this.updateLoading();
                    this.cdr.markForCheck();
                },
                error: (error) => {
                    this.loadingAgeing = false;
                    this.updateLoading();

                    if (error.status === 204) {
                        this.toastService.show('No ageing data found', 'info');
                    }
                    else if (error.status === 404) {
                        this.toastService.show('Matching batch not found', 'error');
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                }
            }
        );
    }

    private updateLoading() {
        this.loading = this.loadingAnnexure || this.loadingAgeing;
    }

    getAnnexureColumnMap() {
        return {
            debtorName: 'Debtor Name',
            debtorCode: 'Code',
            policyReference: 'Reference',
            debtorRegistrationNumber: 'Registration',
            approvedLimit: 'Granted'
        };
    }

    getAgeingColumnMap() {
        return {
            customerName: 'Client Name',
            customerCode: 'Code',
            policyReferenceNumber: 'Reference',
            customerRegistrationNumber: 'Registration',
            total: 'Outstanding'
        };
    }

    getAnnexureGlyphColumn() {
        return 'rowGlyph';
    }

    getAgeingGlyphColumn() {
        return 'rowGlyph';
    }

    onAnnexureRowSelect(row: AnnexureMatchingItem | AnnexureMatchingItem[]): void {
        const selectedRow = Array.isArray(row) ? row[0] : row;

        if (!selectedRow || selectedRow.id === undefined || selectedRow.id === null) {
            this.selectedAnnexureId = 0;
            this.highlightedAgeingIds = [];
        }
        else {
            this.selectedAnnexureId = selectedRow.id;

            if (this.matched) {
                this.navigateToMatchedAgeingRecords(selectedRow.id);
            }
            else {
                this.highlightedAgeingIds = [];
            }
        }

        this.updateSelections(this.selectedAnnexureId, this.selectedAgeingIds);
    }

    onAgeingRowSelect(rows: AgeingMatchingItem[]): void {
        if (!rows || rows.length === 0) {
            this.selectedAgeingIds = [];
            this.highlightedAnnexureIds = [];
            this.updateSelections(this.selectedAnnexureId, this.selectedAgeingIds);

            return;
        }

        const latestSelection: AgeingMatchingItem = rows[rows.length - 1];

        if (this.selectedAgeingIds.includes(latestSelection.id)) {
            this.selectedAgeingIds = this.selectedAgeingIds.filter(id => id !== latestSelection.id);
        }
        else {
            this.selectedAgeingIds.push(latestSelection.id);

            if (this.matched) {
                this.navigateToMatchedAnnexureRecord(latestSelection.id);
            }
        }

        this.updateSelections(this.selectedAnnexureId, this.selectedAgeingIds);
    }

    navigateToMatchedAgeingRecords(annexureDataId: number): void {
        this.loadingService.show();

        // Use new optimized endpoint that returns page number directly
        this.matchingService.getMatchedAgeingPage(
            this.matchingBatchId,
            annexureDataId,
            0, // ageingDataId - we just want to find first matched record
            this.ageingPaging.pageSize,
            this.ageingSearch || undefined,
            this.ageingPaging.sortColumn || undefined,
            this.ageingPaging.sortDirection as any || undefined,
            this.matched,
            this.franchiseLoss,
            this.uncategorized
        ).subscribe({
            next: (result) => {
                if (result && result.matchedAgeingIds && result.matchedAgeingIds.length > 0) {
                    this.highlightedAgeingIds = result.matchedAgeingIds;

                    // Navigate to the page if we got a valid page number and first matched ID
                    if (result.pageNumber && result.matchedAgeingIds[0]) {
                        this.navigateToAgeingPage(result.pageNumber, result.matchedAgeingIds[0]);
                    }
                    else {
                        // Fallback to old behavior if page number not returned
                        this.findAgeingRecordPageAndNavigate(result.matchedAgeingIds[0]);
                    }
                }
                else {
                    // OLD SYSTEM BEHAVIOR: Silently do nothing if no matches found
                    this.highlightedAgeingIds = [];
                    this.loadingService.hide();
                }
                this.cdr.detectChanges();
            },
            error: (error) => {
                // Fallback to old endpoint if new one fails
                this.loadingService.hide();
                this.navigateToMatchedAgeingRecordsOldWay(annexureDataId);
            }
        });
    }

    navigateToMatchedAgeingRecordsOldWay(annexureDataId: number): void {
        const url = `${this.baseUrl}/matching/${this.matchingBatchId}/matched-ageings/${annexureDataId}`;

        this.loadingService.show();

        this.http.get<number[]>(url).subscribe({
            next: (matchedAgeingIds) => {
                if (matchedAgeingIds && matchedAgeingIds.length > 0) {
                    this.highlightedAgeingIds = matchedAgeingIds;
                    this.findAgeingRecordPageAndNavigate(matchedAgeingIds[0]);
                }
                else {
                    // OLD SYSTEM BEHAVIOR: Silently do nothing if no matches found
                    // Don't show warning - just clear highlights
                    this.highlightedAgeingIds = [];
                    this.loadingService.hide();
                }
                this.cdr.detectChanges();
            },
            error: (error) => {
                this.loadingService.hide();
                this.errorService.handleError(error);
                this.highlightedAgeingIds = [];
                this.cdr.detectChanges();
            }
        });
    }

    navigateToAgeingPage(pageNumber: number, ageingDataId: number): void {
        // Check if we're already on the correct page
        if (this.ageingPaging.pageNo === pageNumber) {
            const existsOnCurrentPage = this.ageingRows.some(row => row.id === ageingDataId);
            if (existsOnCurrentPage) {
                this.loadingService.hide();
                this.cdr.detectChanges();
                return;
            }
        }

        // Navigate to the specified page
        this.matchingService.getAgeingMatchingData(
            this.ageingSearch,
            pageNumber,
            this.ageingPaging.pageSize,
            this.ageingPaging.sortDirection,
            this.ageingPaging.sortColumn,
            this.matchingBatchId,
            this.matched,
            this.franchiseLoss,
            this.uncategorized
        ).subscribe({
            next: (data) => {
                this.ageingPaging.pageNo = pageNumber;
                this.ageingRows = (data.results || []).map(row => {
                    if (row['total'] !== null && row['total'] !== undefined) {
                        row['total'] = this.formatCurrency(row['total']);
                    }
                    return row;
                });
                this.ageingPaging = data.pagingData;
                this.loadingService.hide();
                this.cdr.detectChanges();
            },
            error: (error) => {
                this.loadingService.hide();
                this.errorService.handleError(error);
                this.cdr.detectChanges();
            }
        });
    }

    navigateToMatchedAnnexureRecord(ageingDataId: number): void {
        const url = `${this.baseUrl}/matching/${this.matchingBatchId}/matched-annexure/${ageingDataId}`;

        this.loadingService.show();

        this.http.get<number | null>(url).subscribe({
            next: (matchedAnnexureId) => {
                if (matchedAnnexureId) {
                    this.highlightedAnnexureIds = [matchedAnnexureId];
                    this.findAnnexureRecordPageAndNavigate(matchedAnnexureId);
                }
                else {
                    // OLD SYSTEM BEHAVIOR: Silently do nothing if no matches found
                    // Don't show warning - just clear highlights
                    this.highlightedAnnexureIds = [];
                    this.loadingService.hide();
                }
                this.cdr.detectChanges();
            },
            error: (error) => {
                this.loadingService.hide();
                this.errorService.handleError(error);
                this.highlightedAnnexureIds = [];
                this.cdr.detectChanges();
            }
        });
    }

    findAgeingRecordPageAndNavigate(ageingDataId: number): void {
        const existsOnCurrentPage = this.ageingRows.some(row => row.id === ageingDataId);

        if (existsOnCurrentPage) {
            this.loadingService.hide();
            this.cdr.detectChanges();

            return;
        }

        this.searchForAgeingRecordOnPage(ageingDataId, 1);
    }

    searchForAgeingRecordOnPage(ageingDataId: number, pageNo: number): void {
        if (pageNo > this.ageingPaging.pageCount) {
            this.loadingService.hide();
            this.toastService.show('Matched record not found in current filter', 'info');

            return;
        }

        this.matchingService.getAgeingMatchingData(
            this.ageingSearch,
            pageNo,
            this.ageingPaging.pageSize,
            this.ageingPaging.sortDirection,
            this.ageingPaging.sortColumn,
            this.matchingBatchId,
            this.matched,
            this.franchiseLoss,
            this.uncategorized
        ).subscribe({
            next: (data) => {
                const foundOnPage = data.results?.some(row => row.id === ageingDataId);

                if (foundOnPage) {
                    this.ageingPaging.pageNo = pageNo;
                    this.ageingRows = (data.results || []).map(row => {
                        if (row['total'] !== null && row['total'] !== undefined) {
                            row['total'] = this.formatCurrency(row['total']);
                        }

                        return row;
                    });
                    this.ageingPaging = data.pagingData;
                    this.loadingService.hide();
                    this.cdr.detectChanges();
                }
                else {
                    this.searchForAgeingRecordOnPage(ageingDataId, pageNo + 1);
                }
            },
            error: () => {
                this.searchForAgeingRecordOnPage(ageingDataId, pageNo + 1);
            }
        });
    }

    findAnnexureRecordPageAndNavigate(annexureDataId: number): void {
        const existsOnCurrentPage = this.annexureRows.some(row => row.id === annexureDataId);

        if (existsOnCurrentPage) {
            this.loadingService.hide();
            this.cdr.detectChanges();

            return;
        }

        this.searchForAnnexureRecordOnPage(annexureDataId, 1);
    }

    searchForAnnexureRecordOnPage(annexureDataId: number, pageNo: number): void {
        if (pageNo > this.annexurePaging.pageCount) {
            this.loadingService.hide();
            this.toastService.show('Matched record not found in current filter', 'info');

            return;
        }

        this.matchingService.getAnnexureMatchingData(
            this.annexureSearch,
            pageNo,
            this.annexurePaging.pageSize,
            this.annexurePaging.sortDirection,
            this.annexurePaging.sortColumn,
            this.matchingBatchId
        ).subscribe({
            next: (data) => {
                const foundOnPage = data.results?.some(row => row.id === annexureDataId);

                if (foundOnPage) {
                    this.annexurePaging.pageNo = pageNo;
                    this.annexureRows = (data.results || []).map(row => {
                        const newRow = new AnnexureMatchingItem({ ...row });

                        if (newRow.approvedLimit !== null && newRow.approvedLimit !== undefined) {
                            newRow.approvedLimit = this.formatCurrency(newRow.approvedLimit);
                        }

                        return newRow;
                    });
                    this.annexurePaging = data.pagingData;
                    this.loadingService.hide();
                    this.cdr.detectChanges();
                }
                else {
                    this.searchForAnnexureRecordOnPage(annexureDataId, pageNo + 1);
                }
            },
            error: () => {
                this.searchForAnnexureRecordOnPage(annexureDataId, pageNo + 1);
            }
        });
    }

    updateSelections(annexureId: number, ageingIds: number[]): void {
        this.selectedAnnexureId = annexureId;

        this.selectedAgeingIds = ageingIds.length
            ?   ageingIds
            :   this.selectedAgeingIds;

        this.createMatchingDataInput = new CreateMatchingDataInput({
            clientAreaId: this.clientAreaId,
            annexureDataId: this.selectedAnnexureId,
            matchingBatchId: this.matchingBatchId,
            ageingDataIds: this.selectedAgeingIds,
        });
    }

    formatCurrency(amount: number): string {
        if (amount === null || amount === undefined) {
            return '';
        }

        return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount);
    }

    onNext(): Observable<boolean> {
        return scheduled([true], asyncScheduler);
    }

    onPrevious(): Observable<boolean> {
        return scheduled([true], asyncScheduler);
    }

    isValid(): boolean {
        return true;
    }

    isMatchingValid(): boolean {
        if ((!this.selectedAnnexureId || this.selectedAnnexureId === 0) && (!this.selectedAgeingIds || this.selectedAgeingIds.length === 0)) {
            this.toastService.show("Please select one annexure record and at least one ageing record.", "info");
            return false;
        }

        if ((this.selectedAnnexureId && this.selectedAnnexureId !== 0) && (!this.selectedAgeingIds || this.selectedAgeingIds.length === 0)) {
            this.toastService.show("Please select at least one ageing record.", "info");
            return false;
        }

        if ((!this.selectedAnnexureId || this.selectedAnnexureId === 0) && (this.selectedAgeingIds && this.selectedAgeingIds.length > 0)) {
            this.toastService.show("Please select one annexure record.", "info");
            return false;
        }

        return true;
    }

    onMatchedClicked(): void {
        if (!this.isMatchingValid()) {
            return;
        }

        this.loading = true;

        if (this.isValid()) {
            this.matchingService.createMatchingData(this.createMatchingDataInput)
                .subscribe({
                    next: (result) => {
                        if (!result || !Array.isArray(result['createdRecords']) || result['createdRecords'].length === 0) {
                            this.toastService.show("Matching data already exists for selected items", "info");
                            this.loading = false;
                            return;
                        }

                        this.toastService.show("Records matched successfully.", "success");

                        this.ageingRows = [];
                        this.annexureRows = [];

                        this.createMatchingDataInput = new CreateMatchingDataInput({
                            annexureDataId: 0,
                            matchingBatchId: this.matchingBatchId,
                            ageingDataIds: [],
                        });

                        this.refreshCurrentPage();
                        this.loading = false;
                    },
                    error: (error) => {
                        this.toastService.show("An error occurred during matching. Please try again.", "error");
                        this.errorService.handleError(error);
                        this.loading = false;
                    }
                }
            );
        }
        else {
            this.loading = false;
        }
    }

    getMatchedData(): void {
        if (!this.isMatchingValid()) {
            return;
        }

        if (this.selectedAgeingIds.length !== 1) {
            this.toastService.show("Please select only one ageing record to unmatch.", "info");
            return;
        }

        if (!this.matched) {
            this.toastService.show("Please select a matched ageing record", "info");
            return;
        }

        this.matchingService.getMatchedData(this.selectedAnnexureId, this.selectedAgeingIds[0])
            .subscribe({
                next: (result) => {
                    if (result['isMatched']) {
                        this.onUnmatchClick();
                    }
                    else {
                        this.toastService.show("Ageing record is not matched to the selected annexure.", "info");
                    }
                },
                error: (error) => {
                    this.toastService.show("An error occurred while fetching matching data.", "error");
                    this.errorService.handleError(error);
                }
            });
    }

    onUnmatchClick(): void {
        const ageingDataId = this.selectedAgeingIds[0];
        const annexureDataId = this.selectedAnnexureId;

        this.loading = true;

        this.matchingService.deleteMatchingData(annexureDataId, ageingDataId)
            .subscribe({
                next: () => {
                    this.toastService.show("Matching record successfully unmatched.", "success");
                    this.selectedAgeingIds = [];

                    this.refreshCurrentPage();
                    this.loading = false;
                },
                error: (error) => {
                    if (error.status === 204) {
                        this.toastService.show('Matching data not found', 'Info');
                    }
                    else {
                        this.errorService.handleError(error);
                        this.loading = false;
                    }
                }
            }
        );
    }

    onCompleteClick(): void {
        this.matchingService.completeMatchingBatch(this.matchingBatchId)
            .subscribe({
                next: () => {
                    this.toastService.show("Matching batch completed.", "success");
                },
                error: (error) => {
                    this.toastService.show("An error occurred during matching. Please try again.", "error");
                    this.errorService.handleError(error);
                }
            }
        );
    }

    getAnnexureDataById(id: number): void {
        // Find the row in the grid to get matched status
        const gridRow = this.annexureRows.find(row => row.id === id);
        
        this.annexureService.getAnnexureData(id)
            .subscribe({
                next: (result: any) => {
                    this.annexureRecord = result;
                    // Add matched status from grid row
                    if (gridRow) {
                        this.annexureRecord['matched'] = gridRow['matched'];
                        this.annexureRecord['isManual'] = gridRow['isManual'];
                        this.annexureRecord['rowGlyph'] = gridRow['rowGlyph'];
                    }
                    this.isUpdateAnnexure = true;
                },
                error: (error: any) => {
                    this.toastService.show("An error occurred while fetching annexure data.", "error");
                    this.errorService.handleError(error);
                }
            }
        );
    }

    getAgeingDataById(id: number): void {
        // Find the row in the grid to get matched status
        const gridRow = this.ageingRows.find(row => row.id === id);
        
        this.ageingService.getAgeingData(id)
            .subscribe({
                next: (result: any) => {
                    this.ageingRecord = result;
                    // Add matched status from grid row
                    if (gridRow) {
                        this.ageingRecord['matched'] = gridRow['matched'];
                        this.ageingRecord['isManual'] = gridRow['isManual'];
                        this.ageingRecord['rowGlyph'] = gridRow['rowGlyph'];
                    }

                    // Load existing notes for this ageing record
                    if (result.clientAreaId && result.customerCode) {
                        this.matchingService.getAgeingNote(this.matchingBatchId, result.customerCode, result.clientAreaId)
                            .subscribe({
                                next: (note: any) => {
                                    if (note && note.noteId) {
                                        this.selectedNoteId = note.noteId;
                                        // Check if this note has "showOther" enabled
                                        const selectedNote = this.noteOptions.find(n => n.id === note.noteId);
                                        if (selectedNote && selectedNote.showOther && note.otherComments) {
                                            this.showOtherInput = true;
                                            this.ageingRecord['comment'] = note.otherComments;
                                        }
                                    }
                                },
                                error: (error: any) => {
                                    // No existing note found or error - this is OK, just continue
                                    if (error.status !== 204) {
                                        console.warn('Error loading ageing note:', error);
                                    }
                                }
                            });
                    }

                    this.isUpdateAgeing = true;
                },
                error: (error: any) => {
                    this.toastService.show("An error occurred while fetching ageing data.", "error");
                    this.errorService.handleError(error);
                }
            }
        );
    }

    updateAnnexureRecord(): void {
        const input = { approvedLimit: this.annexureRecord.approvedLimit } as UpdateAnnexureDataInput;

        this.annexureService.updateAnnexureData(this.annexureRecord.id, input)
            .subscribe({
                next: (result: any) => {
                    this.isUpdateAnnexure = false;
                    this.toastService.show("Annexure data record updated successfully.", "success");

                    this.getAnnexureData(this.annexurePaging);
                },
                error: (error: any) => {
                    this.toastService.show("An error occurred while saving annexure data.", "error");
                    this.errorService.handleError(error);
                }
            }
        );
    }

    updateAgeingRecord(): void {
        const input = { comment: this.ageingRecord['comment'] } as UpdateAgeingDataInput;

        this.ageingService.updateAgeingData(this.ageingRecord.id, input)
            .subscribe({
                next: (result: any) => {
                    this.isUpdateAgeing = false;
                    this.toastService.show("Ageing data record updated successfully.", "success");

                    if (this.selectedNoteId !== undefined || this.selectedNoteId !== 0) {
                        this.createAgeingNote();
                    }

                    this.getAgeingData(this.ageingPaging);
                },
                error: (error: any) => {
                    this.toastService.show("An error occurred while saving ageing data.", "error");
                    this.errorService.handleError(error);
                }
            }
        );
    }

    createAgeingNote(): void {
        if (!this.selectedNoteId || !this.ageingRecord.clientAreaId || !this.ageingRecord.customerCode) {
            this.toastService.show("Please select a note and ensure the record has valid client area and customer code.", "error");
            return;
        }

        const selectedNote = this.noteOptions.find(note => note.id === this.selectedNoteId);
        let otherComments = null;

        if (selectedNote && selectedNote.showOther && this.showOtherInput) {
            otherComments = this.ageingRecord['comment'] || null;
        }

        const createInput = new CreateAgeingNoteInput({
            noteId: this.selectedNoteId,
            clientAreaId: this.ageingRecord['clientAreaId'],
            customerCode: this.ageingRecord.customerCode,
            otherComments: otherComments || ''
        });

        this.ageingService.createAgeingNote(createInput)
            .subscribe({
                next: (result: any) => {
                    this.selectedNoteId = undefined;
                    this.showOtherInput = false;
                    this.ageingRecord['comment'] = '';
                },
                error: (error: any) => {
                    this.toastService.show("An error occurred while saving the ageing note.", "error");
                    this.errorService.handleError(error);
                }
            }
        );
    }

    onModalCancel(): void {
        this.selectedNoteId = undefined;
        this.showOtherInput = false;

        this.isUpdateAgeing = false;
        this.isUpdateAnnexure = false;

        this.ageingRecord = new AgeingDataResult();
        this.annexureRecord = new AnnexureDataResult();
    }
}
